import { FC, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { SVG_MUI } from "@ryde/assets";
import {
  AccessControl,
  CellSimple,
  StationRouteDurationTooltip,
} from "@ryde/controls";
import {
  StationRouteContext,
  StationRouteStationContext,
  useAreaTitle,
} from "@ryde/redux";
import {
  canReorderRoutingWaypoint,
  getRealStationListOrdinal,
  getStationRouteStationDisplayDurationMin,
  getWaypointCategoryVisual,
  isStationRouteRouteStructureLocked,
  isStationRouteWaypointEditingAllowed,
  isStationRouteWaypointStation,
  NO_VALUE_DISPLAY,
  useIsMobile,
  useTimezone,
  ZERO_TIME_HHmm,
} from "@ryde/shared";
import { rydeColors } from "@ryde/theme";

import { StationRouteWaypointRowBadge } from "../../parts";
import { StationRowButtonsStationRoute } from "./station-route.buttons.row";
import {
  StationBoxCenterStyled,
  StationBoxLeftStyled,
  StationBoxRightStyled,
  StationRouteStationRowStyled,
} from "./station-route.station.styled";
import { StationRouteTimeEdit } from "./station-route.time-edit";

export const StationRowStationRoute: FC = () => {
  const { rideOpenerId } = useContext(StationRouteContext);

  const {
    stationIndex = 0,
    station,
    stationRouteDto,
    attributes,
    listeners,
    isFirstStation,
    isLastStation,
    isDragReorderAllowed = true,
  } = useContext(StationRouteStationContext);

  const {
    name: stationName = "",
    areaId,
    timeArrival,
    isLocked,
  } = station || {};
  const { isMobile } = useIsMobile();
  const { timezone } = useTimezone();
  const { t } = useTranslation();
  /*
  Disabled  RYDE-5186 but may be re-enabled in the future
  const { openEditFormDrawer } = useDrawers();
  */
  const { title: areaTitle } = useAreaTitle(areaId);

  const isSingleStation = useMemo(() => {
    if (isLastStation && isFirstStation) {
      return true;
    }

    return false;
  }, [isFirstStation, isLastStation]);

  const duration = useMemo(() => {
    const durationMin = getStationRouteStationDisplayDurationMin({
      stationRouteDto,
      index: stationIndex,
      timezone,
    });

    if (durationMin === undefined || Number.isNaN(durationMin)) {
      return NO_VALUE_DISPLAY;
    }

    return `${durationMin} ${t("general.MIN")}`;
  }, [stationRouteDto, stationIndex, timezone, t]);
  /*
  Disabled  RYDE-5186 but may be re-enabled in the future
  const handleOpenStationClick = useEvent(() => {
    openEditFormDrawer({
      entity: "station",
      id: stationId,
    });
  }); */

  const isWaypoint = isStationRouteWaypointStation(station);
  const timeEditAllowed = !isWaypoint;
  const waypointVisual = getWaypointCategoryVisual(station?.waypointCategory);
  const isRouteStructureLocked =
    isStationRouteRouteStructureLocked(rideOpenerId);
  const deleteStationAllowed =
    isWaypoint &&
    isStationRouteWaypointEditingAllowed(
      rideOpenerId,
      stationRouteDto?.customized,
    );

  const realStationOrdinal = getRealStationListOrdinal({
    station,
    stations: stationRouteDto?.computedData?.stationInfos,
    listIndex: stationIndex,
  });

  const isStationRouteDraggable = useMemo(() => {
    if (
      !isDragReorderAllowed ||
      isMobile ||
      isLocked ||
      isSingleStation ||
      isRouteStructureLocked
    ) {
      return false;
    }

    if (isWaypoint) {
      return canReorderRoutingWaypoint(
        stationRouteDto?.computedData?.stationInfos,
      );
    }

    return true;
  }, [
    isDragReorderAllowed,
    isLocked,
    isMobile,
    isRouteStructureLocked,
    isSingleStation,
    isWaypoint,
    stationRouteDto?.computedData?.stationInfos,
  ]);

  return (
    <StationRouteStationRowStyled>
      {!isMobile && (
        <div
          className="column_grip"
          {...(isStationRouteDraggable ? { ...attributes, ...listeners } : {})}
        >
          <AccessControl entity="stationRoute" action="update">
            {isStationRouteDraggable && (
              <SVG_MUI.GripVertical3SVG className="pointer" />
            )}
          </AccessControl>
        </div>
      )}
      {/*
      Disabled  RYDE-5186 but may be re-enabled in the future
      <div className="column_map_icon">
        <FramerTooltip content={t("stationRoute.OPEN_STATION")}>
          <SVG_MUI.MapStationCourse4SVG
            className="pointer"
            onClick={handleOpenStationClick}
          />
        </FramerTooltip>
      </div> */}

      <div className="column_station_info">
        <StationBoxLeftStyled>
          {isWaypoint ? (
            <StationRouteWaypointRowBadge
              title={t(waypointVisual.tooltipKey)}
              $category={station?.waypointCategory}
            >
              W
            </StationRouteWaypointRowBadge>
          ) : (
            realStationOrdinal
          )}
        </StationBoxLeftStyled>
        <StationBoxCenterStyled>
          <CellSimple
            color={isWaypoint ? rydeColors.midGrey : rydeColors.black}
            classNamesAdded={isWaypoint ? "waypoint-name" : ""}
          >
            {isWaypoint
              ? t("stationRoute.waypoint.ROW_LABEL", { address: stationName })
              : stationName}
          </CellSimple>
        </StationBoxCenterStyled>
        {timeEditAllowed && <StationRouteTimeEdit />}
        {!timeEditAllowed && (
          <StationBoxRightStyled>
            {timeArrival ? timeArrival : ZERO_TIME_HHmm}
          </StationBoxRightStyled>
        )}
      </div>
      {!isMobile && (
        <div
          className="column_area truncate-text"
          title={
            isWaypoint
              ? t(waypointVisual.tooltipKey)
              : `${areaTitle} (${t("general.ID")}:${areaId})`
          }
        >
          {isWaypoint ? NO_VALUE_DISPLAY : areaTitle}
        </div>
      )}
      <div className="column_duration">
        <StationRouteDurationTooltip
          stationRouteDto={stationRouteDto}
          stationIndex={stationIndex}
        >
          {duration}
        </StationRouteDurationTooltip>
      </div>
      {!isMobile && (
        <div className="column_icons">
          {deleteStationAllowed && <StationRowButtonsStationRoute />}
        </div>
      )}
    </StationRouteStationRowStyled>
  );
};
